import React, { useState, useEffect } from 'react';
import { Bell, Send, Settings, BarChart3, Users, Trash2, Plus, Edit, Eye, CheckCircle } from 'lucide-react';
import api from '../../services/api';

const WebPushNotificationManagement = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  
  // Send notification state
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    icon_url: '',
    image_url: '',
    click_action: '',
    badge_url: '',
    tag: '',
    require_interaction: false,
    silent: false,
    priority: 'normal',
    ttl: 2419200,
    target: 'all',
    target_id: ''
  });

  // Settings state
  const [settings, setSettings] = useState({
    web_push_enabled: false,
    web_push_title: '',
    web_push_icon: '',
    web_push_badge: '',
    web_push_default_message: '',
    web_push_require_permission: true,
    web_push_auto_subscribe: false,
    web_push_show_prompt: true,
    web_push_prompt_text: '',
    web_push_prompt_button_text: '',
    web_push_prompt_delay: 5000,
    web_push_firebase_enabled: false,
    web_push_onesignal_enabled: false
  });

  // Statistics state
  const [stats, setStats] = useState({
    subscriptions: 0,
    notifications: 0,
    recentNotifications: [],
    stats: {
      totalSent: 0,
      totalDelivered: 0,
      totalFailed: 0
    }
  });

  // Notifications list state
  const [notifications, setNotifications] = useState([]);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsLimit] = useState(10);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'send':
          // Load settings for form defaults
          await loadSettings();
          break;
        case 'settings':
          await loadSettings();
          break;
        case 'statistics':
          await loadStatistics();
          break;
        case 'notifications':
          await loadNotifications();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      if (response.data.success) {
        const webPushSettings = {};
        Object.keys(response.data.data).forEach(key => {
          if (key.startsWith('web_push_')) {
            webPushSettings[key] = response.data.data[key];
          }
        });
        setSettings(webPushSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/web-push/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get(`/web-push/notifications?limit=${notificationsLimit}&offset=${(notificationsPage - 1) * notificationsLimit}`);
      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/web-push/send', notificationForm);
      
      if (response.data.success) {
        showMessage('Notification sent successfully!', 'success');
        setNotificationForm({
          title: '',
          message: '',
          icon_url: '',
          image_url: '',
          click_action: '',
          badge_url: '',
          tag: '',
          require_interaction: false,
          silent: false,
          priority: 'normal',
          ttl: 2419200,
          target: 'all',
          target_id: ''
        });
      } else {
        showMessage(response.data.message || 'Failed to send notification', 'error');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      showMessage('Failed to send notification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOneSignalNotification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/web-push/send-onesignal', {
        title: notificationForm.title,
        message: notificationForm.message,
        url: notificationForm.click_action,
        icon: notificationForm.icon_url,
        image: notificationForm.image_url
      });
      
      if (response.data.success) {
        showMessage('OneSignal notification sent successfully!', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send OneSignal notification', 'error');
      }
    } catch (error) {
      console.error('Error sending OneSignal notification:', error);
      showMessage('Failed to send OneSignal notification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setMessage('');

    try {
      const settingsData = {};
      Object.keys(settings).forEach(key => {
        settingsData[key] = settings[key];
      });

      const response = await api.post('/admin/settings', settingsData);
      
      if (response.data.success) {
        showMessage('Settings saved successfully!', 'success');
      } else {
        showMessage('Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('Failed to save settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.delete(`/web-push/notifications/${id}`);
      
      if (response.data.success) {
        showMessage('Notification deleted successfully!', 'success');
        await loadNotifications();
      } else {
        showMessage('Failed to delete notification', 'error');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      showMessage('Failed to delete notification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleFormChange = (field, value) => {
    setNotificationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const tabs = [
    { id: 'send', label: 'Send Notification', icon: Send },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'statistics', label: 'Statistics', icon: BarChart3 },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Web Push Notifications
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage web push notifications and subscriber engagement
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 sm:space-x-8 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
            : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* Content */}
      <div className="space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!loading && (
          <>
            {/* Send Notification Tab */}
            {activeTab === 'send' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Send Web Push Notification
                  </h3>
                  
                  <form onSubmit={handleSendNotification} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Title *
                        </label>
                        <input
                          type="text"
                          required
                          value={notificationForm.title}
                          onChange={(e) => handleFormChange('title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Notification title"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Priority
                        </label>
                        <select
                          value={notificationForm.priority}
                          onChange={(e) => handleFormChange('priority', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Message *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={notificationForm.message}
                        onChange={(e) => handleFormChange('message', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Notification message"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Icon URL
                        </label>
                        <input
                          type="url"
                          value={notificationForm.icon_url}
                          onChange={(e) => handleFormChange('icon_url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="https://example.com/icon.png"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Image URL
                        </label>
                        <input
                          type="url"
                          value={notificationForm.image_url}
                          onChange={(e) => handleFormChange('image_url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Click Action URL
                        </label>
                        <input
                          type="url"
                          value={notificationForm.click_action}
                          onChange={(e) => handleFormChange('click_action', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="https://example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tag
                        </label>
                        <input
                          type="text"
                          value={notificationForm.tag}
                          onChange={(e) => handleFormChange('tag', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="notification-tag"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Target
                        </label>
                        <select
                          value={notificationForm.target}
                          onChange={(e) => handleFormChange('target', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">All Users</option>
                          <option value="user">Specific User</option>
                          <option value="subscription">Specific Subscription</option>
                        </select>
                      </div>
                      
                      {(notificationForm.target === 'user' || notificationForm.target === 'subscription') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Target ID
                          </label>
                          <input
                            type="text"
                            value={notificationForm.target_id}
                            onChange={(e) => handleFormChange('target_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder={notificationForm.target === 'user' ? 'User ID' : 'Subscription ID'}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationForm.require_interaction}
                          onChange={(e) => handleFormChange('require_interaction', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Require Interaction
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationForm.silent}
                          onChange={(e) => handleFormChange('silent', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Silent
                        </span>
                      </label>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Send Notification
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleSendOneSignalNotification}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Send via OneSignal
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Web Push Notification Settings
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          Enable Web Push Notifications
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Enable or disable web push notifications globally
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.web_push_enabled}
                          onChange={(e) => handleSettingsChange('web_push_enabled', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Default Title
                        </label>
                        <input
                          type="text"
                          value={settings.web_push_title}
                          onChange={(e) => handleSettingsChange('web_push_title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Default Icon
                        </label>
                        <input
                          type="text"
                          value={settings.web_push_icon}
                          onChange={(e) => handleSettingsChange('web_push_icon', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Default Message
                      </label>
                      <textarea
                        rows={3}
                        value={settings.web_push_default_message}
                        onChange={(e) => handleSettingsChange('web_push_default_message', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          Show Permission Prompt
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Automatically show permission prompt to users
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.web_push_show_prompt}
                          onChange={(e) => handleSettingsChange('web_push_show_prompt', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Prompt Text
                      </label>
                      <input
                        type="text"
                        value={settings.web_push_prompt_text}
                        onChange={(e) => handleSettingsChange('web_push_prompt_text', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={handleSaveSettings}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save Settings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'statistics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:p-6">
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Subscriptions</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{stats.subscriptions}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                    <div className="flex items-center">
                      <Bell className="h-8 w-8 text-green-600 dark:text-green-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Notifications</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{stats.notifications}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                    <div className="flex items-center">
                      <BarChart3 className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Sent</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{stats.stats.totalSent}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                    <div className="flex items-center">
                      <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Delivered</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{stats.stats.totalDelivered}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Recent Notifications
                  </h3>
                  
                  <div className="space-y-4">
                    {stats.recentNotifications.map((notification) => (
                      <div key={notification.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {notification.title}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Sent: {notification.sent_count}
                            </p>
                            <p className="text-sm text-green-600 dark:text-green-400">
                              Delivered: {notification.delivered_count}
                            </p>
                            <p className="text-sm text-red-600 dark:text-red-400">
                              Failed: {notification.failed_count}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Notification History
                  </h3>
                  
                  <div className="space-y-4">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {notification.title}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400 dark:text-gray-500">
                              <span>Sent: {notification.sent_count}</span>
                              <span className="text-green-600 dark:text-green-400">Delivered: {notification.delivered_count}</span>
                              <span className="text-red-600 dark:text-red-400">Failed: {notification.failed_count}</span>
                              <span>{new Date(notification.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WebPushNotificationManagement;
